'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import {
  getNotificationSettings,
  type NotificationSetting,
  type NotificationSettingsResponse,
  updateNotificationSettings
} from '../api/notificationsApi'
import { notificationsQueryKeys } from '../notifications.constants'
import { useNotificationsUserId } from './useNotificationsUserId'

type SettingChange = Pick<
  NotificationSetting,
  'category' | 'channel' | 'enabled'
>

const PUSH_CHANNEL = 'push'

const applyChanges = (
  settings: NotificationSetting[],
  changes: SettingChange[]
) =>
  settings.map(setting => {
    const change = changes.find(
      item =>
        item.category === setting.category && item.channel === setting.channel
    )
    return change ? { ...setting, enabled: change.enabled } : setting
  })

export function useNotificationSettings(enabled = true) {
  const userId = useNotificationsUserId()

  return useQuery({
    queryKey: notificationsQueryKeys.settings(userId ?? ''),
    enabled: enabled && Boolean(userId),
    queryFn: getNotificationSettings,
    select: data => data.settings ?? []
  })
}

/**
 * Saves switches. The cache changes only once the server has stored them: an
 * unsaved change is never shown as applied (BAT-989), and the last saved
 * value stays in effect when saving fails.
 */
export function useUpdateNotificationSettings() {
  const queryClient = useQueryClient()
  const userId = useNotificationsUserId()

  return useMutation({
    mutationFn: (settings: SettingChange[]) =>
      updateNotificationSettings(settings),
    onSuccess: (data, changes) => {
      if (!userId) {
        return
      }

      const key = notificationsQueryKeys.settings(userId)
      if (data?.settings) {
        queryClient.setQueryData(key, data)
        return
      }

      queryClient.setQueryData<NotificationSettingsResponse>(key, previous =>
        previous
          ? {
              ...previous,
              settings: applyChanges(previous.settings ?? [], changes)
            }
          : previous
      )
      void queryClient.invalidateQueries({ queryKey: key })
    }
  })
}

export interface PushEnabledState {
  /** The account's "Receive Web Push" switch. */
  enabled: boolean
  isLoading: boolean
  /** Loading failed, or the last save failed (the saved value stays). */
  isError: boolean
  isSaving: boolean
  /** The value being saved; `enabled` keeps the saved one until then. */
  pendingValue: boolean | undefined
  /** Resolves true once saved; false when it failed. */
  setEnabled: (value: boolean) => Promise<boolean>
}

/**
 * The single Web Push switch of BAT-989. The service still keeps a push
 * setting per category, so the switch writes one value into all of them —
 * mandatory ones included, since "mandatory" binds email only. It reads as on
 * while any push category still delivers.
 */
export function usePushEnabled(): PushEnabledState {
  const queryClient = useQueryClient()
  const userId = useNotificationsUserId()
  const settings = useNotificationSettings()
  const update = useUpdateNotificationSettings()
  const { mutateAsync } = update

  const enabled = useMemo(
    () =>
      (settings.data ?? []).some(
        setting => setting.channel === PUSH_CHANNEL && setting.enabled
      ),
    [settings.data]
  )

  const setEnabled = useCallback(
    async (value: boolean) => {
      if (!userId) {
        return false
      }

      const current =
        queryClient.getQueryData<NotificationSettingsResponse>(
          notificationsQueryKeys.settings(userId)
        )?.settings ?? []
      const changes = current
        .filter(setting => setting.channel === PUSH_CHANNEL)
        .map(({ category, channel }) => ({ category, channel, enabled: value }))

      if (changes.length === 0) {
        return false
      }

      try {
        await mutateAsync(changes)
        return true
      } catch {
        return false
      }
    },
    [mutateAsync, queryClient, userId]
  )

  return {
    enabled,
    isLoading: settings.isLoading,
    isError: settings.isError || update.isError,
    isSaving: update.isPending,
    pendingValue: update.isPending ? update.variables?.[0]?.enabled : undefined,
    setEnabled
  }
}
